import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/auth";
import { Toaster } from "@/components/ui/sonner";

import AppLayout from "@/components/AppLayout";
import ErrorBoundary from "@/components/ErrorBoundary";
import Landing from "@/pages/Landing";
import AuthCallback from "@/pages/AuthCallback";
import Home from "@/pages/Home";
import Memora from "@/pages/Memora";
import Add from "@/pages/Add";
import Trends from "@/pages/Trends";
import ObservationDetail from "@/pages/ObservationDetail";
import Records from "@/pages/Records";
import Timeline from "@/pages/Timeline";
import Documents from "@/pages/Documents";
import EventsList from "@/pages/EventsList";
import EventDetail from "@/pages/EventDetail";
import Profile from "@/pages/Profile";

function Protected({ children }) {
  const { status } = useAuth();
  const location = useLocation();
  if (status === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50">
        <div className="text-neutral-500 text-sm">Loading…</div>
      </div>
    );
  }
  if (status !== "authed") return <Navigate to="/login" state={{ from: location }} replace />;
  return children;
}

function RootRoute() {
  const { status } = useAuth();
  // Handle Emergent OAuth callback (session_id in URL hash)
  if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  if (status === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50">
        <div className="text-neutral-500 text-sm">Loading…</div>
      </div>
    );
  }
  // Landing is the entry for everyone; authed users see a "Continue to Memora" CTA
  return <Landing />;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            <Route path="/" element={<RootRoute />} />
            <Route path="/welcome" element={<Navigate to="/" replace />} />
            {/* Sign-in retired for now — routes redirect back to landing */}
            <Route path="/login" element={<Navigate to="/" replace />} />
            <Route path="/signup" element={<Navigate to="/" replace />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route element={<Protected><AppLayout /></Protected>}>
              <Route path="/home" element={<ErrorBoundary><Home /></ErrorBoundary>} />
              <Route path="/memora" element={<ErrorBoundary><Memora /></ErrorBoundary>} />
              <Route path="/add" element={<ErrorBoundary><Add /></ErrorBoundary>} />
              <Route path="/trends" element={<ErrorBoundary><Trends /></ErrorBoundary>} />
              <Route path="/observation/:canonical_type" element={<ErrorBoundary><ObservationDetail /></ErrorBoundary>} />
              <Route path="/records" element={<ErrorBoundary><Records /></ErrorBoundary>} />
              <Route path="/records/timeline" element={<ErrorBoundary><Timeline /></ErrorBoundary>} />
              <Route path="/records/documents" element={<ErrorBoundary><Documents /></ErrorBoundary>} />
              <Route path="/records/events" element={<ErrorBoundary><EventsList /></ErrorBoundary>} />
              <Route path="/events/:id" element={<ErrorBoundary><EventDetail /></ErrorBoundary>} />
              <Route path="/profile" element={<ErrorBoundary><Profile /></ErrorBoundary>} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <Toaster position="top-center" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
